import { FC, useEffect, useState } from "react";
import ButtonIcon from "@/components/UI/Buttons/ButtonIcon";
import Container from "@/components/Container";
import Star from "@/assets/icons/FilledStar";
import useForm from "@/custom_hooks/useForm";
import useNewslettersSubscription from "@/custom_hooks/useNewslettersSubcription";

interface ReviewFormProps {
  className?: string;
  isMobile: boolean;
}

const ReviewForm: FC<ReviewFormProps> = ({ isMobile }) => {
  const baseApiUrl = import.meta.env.VITE_BASE_API_URL;

  const [ratingSelected, setRatingSelected] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {
    formData,
    formMessages: {
      setErrorMsg,
      setSuccessMsg,
      successMsg,
      errorMsg,
      clearMessages,
    },
    onInputChange,
  } = useForm({ full_name: "", email: "", review: "", rating: 0 });
  const { isValid, ConfirmationCheckbox } = useNewslettersSubscription(
    setErrorMsg,
    setSuccessMsg,
    setIsLoading,
  );

  useEffect(() => {
    clearMessages();
  }, [ratingSelected]);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    clearMessages();

    const subscriptionErrors = isValid(formData.email);
    if (subscriptionErrors) {
      setErrorMsg(subscriptionErrors);
    } else if (ratingSelected === 0) {
      setErrorMsg("Pasirinkite įvertinimą");
    } else {
      setIsLoading(true);
      formData.rating = ratingSelected;
      try {
        const res: any = await fetch(baseApiUrl + "/reviews", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(formData),
        });

        const data = await res.json();
        if (res.ok) {
          if (data.message) {
            setSuccessMsg(data.message);
          } else {
            setSuccessMsg("Atsiliepimas gautas.");
          }
        } else if (res.status === 500) {
          setErrorMsg("Įvyko sistemos klaida, pabandykite vėliau");
        } else if (res.status === 422 && data.message) {
          setErrorMsg(data.message);
        } else {
          setErrorMsg("Įvyko sistemos klaida, pabandykite vėliau");
        }
      } catch {
        setErrorMsg("Įvyko sistemos klaida, pabandykite vėliau");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <section className="mb-10 flex flex-col px-4 md:px-6">
        <Container>
          <div className="flex w-full flex-col">
            <section className="mb-8 grid md:justify-center">
              <div className="bg-linear-gradient text-tertiary grid rounded-[22px] px-[22px] py-[50px] md:max-w-[600px] md:px-[50px] md:py-[60px]">
                <h2 className="text-sand">
                  Norite palikti atsiliepimą apie Telomera?
                </h2>
                <p className="text-tertiary mt-[20px] md:text-lg">
                  Džiaugiamės, kad pasirinkote Telomera. Jei turite ką pasakyti
                  – gerą žodį, pastebėjimą ar pasiūlymą – labai lauksime jūsų
                  atsiliepimo. Tai padeda mums augti.
                </p>

                <div className="flex justify-center pt-[30px]">
                  <button
                    className="cursor-pointer"
                    onClick={() => {
                      setRatingSelected(1);
                    }}
                    disabled={isLoading}
                  >
                    <Star
                      width={38}
                      height={38}
                      color={
                        ratingSelected === 0
                          ? "var(--color-grey-200)"
                          : "#e4c425"
                      }
                    />
                  </button>
                  <button
                    className="cursor-pointer"
                    onClick={() => {
                      setRatingSelected(2);
                    }}
                    disabled={isLoading}
                  >
                    <Star
                      width={38}
                      height={38}
                      color={
                        ratingSelected < 2 ? "var(--color-grey-200)" : "#e4c425"
                      }
                    />
                  </button>
                  <button
                    className="cursor-pointer"
                    onClick={() => {
                      setRatingSelected(3);
                    }}
                    disabled={isLoading}
                  >
                    <Star
                      width={38}
                      height={38}
                      color={
                        ratingSelected < 3 ? "var(--color-grey-200)" : "#e4c425"
                      }
                    />
                  </button>
                  <button
                    className="cursor-pointer"
                    onClick={() => {
                      setRatingSelected(4);
                    }}
                    disabled={isLoading}
                  >
                    <Star
                      width={38}
                      height={38}
                      color={
                        ratingSelected < 4 ? "var(--color-grey-200)" : "#e4c425"
                      }
                    />
                  </button>
                  <button
                    className="cursor-pointer"
                    onClick={() => {
                      setRatingSelected(5);
                    }}
                    disabled={isLoading}
                  >
                    <Star
                      width={38}
                      height={38}
                      color={
                        ratingSelected < 5 ? "var(--color-grey-200)" : "#e4c425"
                      }
                    />
                  </button>
                </div>
                <p className="text-sand mt-[14px] mb-[30px] text-center text-sm">
                  Pasirinkite įvertinimą
                </p>

                <form onSubmit={handleSubmit}>
                  <div className="text-sand mb-4 flex flex-col gap-8">
                    <div className="flex flex-col gap-2">
                      <label className="md:text-lg" htmlFor="full_name">
                        Vardas ir pavardė*
                      </label>
                      <input
                        className="md:bg-sand md:text-grey md:shadow-grey-400 md:shadow-stroke inline-flex h-[53px] w-full items-center justify-center rounded-[10px] px-[10px] text-[18px] leading-none shadow-[0_0_0_1px] outline-none focus:shadow-[0_0_0_2px]"
                        id="full_name"
                        disabled={isLoading}
                        name="full_name"
                        value={formData.full_name}
                        onChange={onInputChange}
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="md:text-lg" htmlFor="email">
                        El. paštas*
                      </label>
                      <input
                        className="md:bg-sand md:text-grey md:shadow-grey-400 md:shadow-stroke inline-flex h-[53px] w-full items-center justify-center rounded-[10px] px-[10px] text-[18px] leading-none shadow-[0_0_0_1px] outline-none focus:shadow-[0_0_0_2px]"
                        id="email"
                        disabled={isLoading}
                        name="email"
                        value={formData.email}
                        onChange={onInputChange}
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="md:text-lg" htmlFor="review">
                        Atsiliepimas
                      </label>
                      <textarea
                        rows={3}
                        className="md:bg-sand md:text-grey md:shadow-grey-400 md:shadow-stroke inline-flex h-[133px] w-full items-center justify-center rounded-[10px] p-[10px] text-[18px] leading-none shadow-[0_0_0_1px] outline-none focus:shadow-[0_0_0_2px]"
                        id="review"
                        disabled={isLoading}
                        name="review"
                        value={formData.review}
                        onChange={onInputChange}
                      />
                    </div>
                    <ConfirmationCheckbox />
                  </div>

                  <div className="flex flex-col items-center justify-center">
                    {successMsg && !errorMsg && (
                      <p className="mt-2 text-green-400">{successMsg}</p>
                    )}
                    {errorMsg && !successMsg && (
                      <p className="mt-2 text-red-300">{errorMsg}</p>
                    )}
                  </div>
                  <div className="mt-9 flex w-full justify-center">
                    <ButtonIcon
                      disabled={isLoading}
                      submitType={true}
                      iconWidth={isMobile ? 18 : 24}
                      iconHeight={isMobile ? 19 : 25}
                      label="Palikti atsiliepimą"
                      icon="rate"
                      theme="dark"
                    />
                  </div>
                </form>
              </div>
            </section>
          </div>
        </Container>
      </section>
    </>
  );
};

export default ReviewForm;
